import { Box, useDisclosure, VStack } from '@chakra-ui/react';
import { useTransactionNotificationLogsQuery } from '@client/graphql/__generated__/main-operations';
import {
  NotificationLogFilters,
  NotificationType,
  TransactionNotificationLogFragment,
  TransactionStageType,
} from '@client/graphql/__generated__/types';
import { useInfiniteQuery } from '@document/hooks/useInfiniteQuery';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppContext } from '~/services/main/contexts/AppContext';
import {
  NotificationDetailModal,
  NotificationsFilters,
  NotificationsTable,
} from './components/NotificationsList';

export const NotificationsListPage = () => {
  const { selectedOrganization } = useAppContext();
  const [searchParams] = useSearchParams();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedNotification, setSelectedNotification] =
    useState<TransactionNotificationLogFragment | null>(null);

  // Parse filters from URL params
  const filters: NotificationLogFilters = useMemo(() => {
    const normalizeParam = (value: string | null): string | undefined =>
      value === null || value === '' ? undefined : value;

    const parseDate = (value: string | null): Date | undefined => {
      if (value === null || value === '') {
        return undefined;
      }
      const parsed = new Date(value);

      return Number.isNaN(parsed.getTime()) ? undefined : parsed;
    };

    const parseEnum = <T extends string>(
      value: string | null,
      enumValues: readonly T[]
    ): T | undefined =>
      value !== null && enumValues.includes(value as T)
        ? (value as T)
        : undefined;

    const eventType = searchParams.get('eventType');
    const notificationType = searchParams.get('notificationType');
    const recipientId = searchParams.get('recipientId');
    const dateFrom = searchParams.get('dateFrom');
    const dateTo = searchParams.get('dateTo');
    const title = searchParams.get('title');

    return {
      eventType: parseEnum(
        eventType,
        Object.values(TransactionStageType) as TransactionStageType[]
      ),
      notificationType: parseEnum(
        notificationType,
        Object.values(NotificationType) as NotificationType[]
      ),
      recipientId: normalizeParam(recipientId),
      dateFrom: parseDate(dateFrom),
      dateTo: parseDate(dateTo),
      title: normalizeParam(title),
    };
  }, [searchParams]);

  const [triggerRef, { data, loading }] = useInfiniteQuery(
    useTransactionNotificationLogsQuery,
    {
      variables: {
        organizationId: selectedOrganization?.id ?? '',
        filters,
        orderBy: '-sentAt',
        perPage: 20,
      },
      skip: !selectedOrganization?.id,
    }
  );

  const notifications = useMemo(
    () => data?.transactionNotificationLogs?.results ?? [],
    [data]
  );

  const handleViewDetails = (
    notification: TransactionNotificationLogFragment
  ) => {
    setSelectedNotification(notification);
    onOpen();
  };

  const handleCloseModal = () => {
    onClose();
    setSelectedNotification(null);
  };

  return (
    <VStack alignItems="stretch" spacing={0}>
      <NotificationsFilters />

      <Box flex={1}>
        <NotificationsTable
          loading={loading}
          notifications={notifications}
          triggerRef={triggerRef}
          onViewDetails={handleViewDetails}
        />
      </Box>

      <NotificationDetailModal
        isOpen={isOpen}
        notification={selectedNotification}
        onClose={handleCloseModal}
      />
    </VStack>
  );
};
